import { motion } from "framer-motion";
import KeyButton from "@/components/KeyButton";
import { scrollToSection } from "@/components/Nav";

const EASE = [0.16, 1, 0.3, 1];

const PACKAGES = [
  {
    id: "01",
    name: "GET STARTED",
    was: "₹50,000",
    price: "₹40,000",
    popular: false,
    features: [
      "9 reels",
      "Content ideation and scripting",
      "Monthly content strategy",
      "Monthly performance report",
      "2 rounds of revisions",
    ],
  },
  {
    id: "02",
    name: "GROWTH",
    was: "₹72,000",
    price: "₹60,000",
    popular: true,
    features: [
      "12 reels",
      "Advanced content ideation and scripting",
      "Content strategy and competitor research",
      "Monthly performance report and recommendations",
      "2 rounds of revisions",
    ],
  },
  {
    id: "03",
    name: "DOMINATE",
    was: "₹95,000",
    price: "₹80,000",
    popular: false,
    features: [
      "16 reels",
      "In-depth content ideation and scripting",
      "Advanced content strategy and competitor analysis",
      "Detailed monthly performance reporting and optimization",
      "2 rounds of revisions",
    ],
  },
];

const PackageCam = ({ pkg, index }) => (
  <motion.div
    data-testid={`package-cam-${pkg.id}`}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: index * 0.12, ease: EASE }}
    className={`group h-full select-none ${pkg.popular ? "lg:-translate-y-4" : ""}`}
  >
    <div
      className={`flex h-full flex-col rounded-[16px] p-[7px] transition-all duration-500 ease-out group-hover:-translate-y-1.5 ${
        pkg.popular
          ? "shadow-[0_30px_50px_-20px_rgba(13,13,13,0.65)]"
          : "shadow-[0_20px_40px_-18px_rgba(13,13,13,0.5)]"
      }`}
      style={{
        background: pkg.popular
          ? "linear-gradient(145deg,#2a2a28 0%,#101010 40%,#333331 60%,#0b0b0b 100%)"
          : "linear-gradient(145deg,#ececea 0%,#c6c6c3 38%,#f4f4f2 58%,#b3b3b0 100%)",
      }}
    >
      <div className="flex flex-1 rounded-[10px] bg-[#101010] p-[6px] shadow-[inset_0_1px_3px_rgba(0,0,0,0.9)]">
        <div className="flex w-full flex-col rounded-[4px] bg-[#EDEFE6] px-6 py-7">
          <div className="flex items-center justify-between">
            <span className="font-mono-label text-[9px] tracking-[0.25em] text-[#8C8B85]">
              ({pkg.id})
            </span>
            {pkg.popular && (
              <span
                data-testid="package-popular-tag"
                className="bg-[#0D0D0D] px-3 py-1 font-mono-label text-[8px] tracking-[0.25em] text-[#F4F3EF]"
              >
                MOST POPULAR
              </span>
            )}
          </div>
          <h3 className="mt-4 font-helvetica text-2xl font-bold tracking-tight text-[#0D0D0D]">
            {pkg.name}
          </h3>
          <div className="mt-2">
            <p className="font-helvetica text-base font-semibold text-[#8C8B85]">
              <span className="line-through decoration-[#0D0D0D]/50 decoration-2">
                {pkg.was}
              </span>
            </p>
            <p className="font-helvetica text-3xl font-bold tracking-tight text-[#0D0D0D]">
              {pkg.price}
              <span className="text-sm font-medium text-[#4A4944]"> / month</span>
            </p>
            <p className="mt-1.5 font-mono-label text-[8px] tracking-[0.25em] text-[#8C8B85]">
              FOR THE FIRST 2 MONTHS
            </p>
          </div>
          <ul className="mt-6 space-y-2.5 border-t border-[#0D0D0D]/10 pt-6">
            {pkg.features.map((f) => (
              <li
                key={f}
                className="flex gap-2.5 font-helvetica text-[13px] leading-snug text-[#2A2A28]"
              >
                <span className="mt-[7px] h-[3px] w-[3px] shrink-0 rounded-full bg-[#0D0D0D]" />
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-auto pt-7">
            <KeyButton
              testid={`package-select-${pkg.id}`}
              compact
              onClick={() => scrollToSection("#contact")}
              className="w-full"
            >
              SELECT {pkg.name}
            </KeyButton>
          </div>
        </div>
      </div>
      <div className="mt-[6px] flex items-center justify-between px-[4px] pb-[2px]">
        <span
          className="block h-[12px] w-[12px] rounded-full shadow-[inset_0_1px_2px_rgba(255,255,255,0.9),0_1px_2px_rgba(0,0,0,0.35)]"
          style={{
            background:
              "conic-gradient(from 210deg,#d8d8d5,#9a9a97,#e9e9e6,#a5a5a2,#d8d8d5)",
          }}
        />
        <span
          className="block h-[5px] w-[5px] rounded-full shadow-[inset_0_1px_1px_rgba(255,255,255,0.8)]"
          style={{ background: "radial-gradient(circle at 35% 30%,#f0f0ee,#8f8f8c)" }}
        />
        <span className="block h-[9px] w-[3px] rounded-[2px] bg-[#8d8d8a] shadow-[inset_0_1px_1px_rgba(255,255,255,0.7)]" />
      </div>
    </div>
  </motion.div>
);

export default function Packages() {
  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="relative overflow-hidden px-6 py-24 sm:px-10 sm:py-32"
    >
      <img
        src="/metal-texture.jpg"
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#F4F3EF] via-transparent to-[#F4F3EF]" />

      <div className="relative mx-auto max-w-[1400px]">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#4A4944] sm:text-xs"
        >
          (04) — Packages
        </motion.p>
        <motion.h2
          data-testid="packages-heading"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
          className="mt-6 font-helvetica text-5xl font-bold tracking-tight text-[#0D0D0D] sm:text-6xl lg:text-7xl"
        >
          PACKAGES
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mt-4 max-w-md font-helvetica text-sm leading-relaxed text-[#4A4944]"
        >
          Monthly retainers for creators and businesses — pick a pace, we
          handle the rest.
        </motion.p>

        <div className="mt-14 grid items-stretch gap-8 md:grid-cols-3 md:gap-6 lg:gap-8">
          {PACKAGES.map((pkg, i) => (
            <PackageCam key={pkg.id} pkg={pkg} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
